import React, { useState, useEffect, useRef } from 'react';
import { FiCheckCircle, FiXCircle, FiPlus, FiCalendar, FiClock } from 'react-icons/fi';
import apiClient from '../apis/apiClient';
import './AdminTableManagement.css';

const AdminTableManagement = () => {
  const [tables, setTables] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [showAddTableForm, setShowAddTableForm] = useState(false);
  const [newTableCount, setNewTableCount] = useState(1);
  const [newTableCapacity, setNewTableCapacity] = useState(2);


  const selectedDateRef = useRef(selectedDate);
  const selectedTimeRef = useRef(selectedTime);

  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);
  useEffect(() => { selectedTimeRef.current = selectedTime; }, [selectedTime]);

  const fetchTables = async () => {
    try {
      const response = await apiClient.get('/tables');
      const data = response.data;
      setTables(data.sort((a, b) => a.id - b.id));
    } catch (error) {
      console.error('Error fetching tables:', error);
      setMessage('Error fetching tables from server.');
    }
  };

  const fetchReservations = async (forDate = selectedDateRef.current, forTime = selectedTimeRef.current) => {
    if (!forDate) {
      setReservations([]);
      return;
    }

    try {
      const response = await apiClient.get('/reservations');
      const data = response.data;

      const filtered = data.filter(
        (r) => r.date === forDate && (forTime ? r.time === forTime : true)
      );
      setReservations(filtered);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      setMessage('Error fetching reservations from server.');
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        await fetchTables();
        if (selectedDateRef.current) await fetchReservations();
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    (async () => {
      setMessage('');
      setLoading(true);
      try {
        await fetchReservations(selectedDate, selectedTime);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedDate, selectedTime]);

  useEffect(() => {
    const intervalMs = 30000;
    const id = setInterval(async () => {
      await fetchTables();
      if (selectedDateRef.current) await fetchReservations();
    }, intervalMs);
    return () => clearInterval(id);
  }, []);

  const isTableBooked = (tableNumber) => {
    return reservations.some((r) => r.table?.tableNumber === tableNumber);
  };

  const getReservationInfo = (tableNumber) => {
    const res = reservations.find((r) => r.table?.tableNumber === tableNumber);
    return res ? `Booked by ${res.name} (${res.guests} guests) at ${res.time}` : 'Free';
  };

  const handleAddTables = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      await apiClient.post('/tables/bulk', { count: newTableCount, capacity: newTableCapacity });

      setMessage(`${newTableCount} table(s) with capacity ${newTableCapacity} added.`);
      await fetchTables();
      setShowAddTableForm(false);
    } catch (error) {
      console.error('Error adding tables:', error);
      setMessage('Server error while adding tables.');
    }
  };

  return (
    <div className="admin-tables-container">
      <div className="admin-header">
        <h2>Table Management</h2>

        <div className="admin-filters">
          <label className="filter-item">
            <FiCalendar /> Date:
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              required
              disabled={loading}
            />
          </label>

          <label className="filter-item">
            <FiClock /> Time:
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              disabled={loading}
            />
          </label>

          <button
            className="add-table-btn"
            onClick={() => setShowAddTableForm(!showAddTableForm)}
            disabled={loading}
          >
            <FiPlus /> {showAddTableForm ? 'Close' : 'Add Table'}
          </button>
        </div>
      </div>

      {message && <p className="feedback-message">{message}</p>}

      {loading ? (
        <p>Loading...</p>
      ) : tables.length === 0 ? (
        <p>No tables available.</p>
      ) : (
        <table className="tables-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Table Number</th>
              <th>Capacity</th>
              <th>Status</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {tables.map((t, index) => {
              const booked = isTableBooked(t.tableNumber);
              return (
                <tr key={t.id} className={booked ? 'booked-row' : ''}>
                  <td>{index + 1}</td>
                  <td>{t.tableNumber}</td>
                  <td>{t.capacity}</td>
                  <td>
                    {booked ? (
                      <span className="status unavailable">
                        <FiXCircle /> Booked
                      </span>
                    ) : (
                      <span className="status available">
                        <FiCheckCircle /> Free
                      </span>
                    )}
                  </td>
                  <td>{getReservationInfo(t.tableNumber)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showAddTableForm && (
        <div className="add-table-form">
          <h3>Add New Tables</h3>
          <form onSubmit={handleAddTables}>
            <div className="form-row">
              <div className="form-group">
                <label>Number of Tables</label>
                <input
                  type="number"
                  value={newTableCount}
                  onChange={(e) => setNewTableCount(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  required
                />
              </div>
              <div className="form-group">
                <label>Seats per Table</label>
                <input
                  type="number"
                  value={newTableCapacity}
                  onChange={(e) => setNewTableCapacity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  required
                />
              </div>
            </div>
            <button type="submit" className="submit-btn">Add Tables</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminTableManagement;